import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PageHeader, DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import merchantService from '../services/merchantService';

const TopupCashbackPage = () => {
  const hasInitialLoadRef = useRef(false);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [topupFormData, setTopupFormData] = useState({
    merchant: null,
    topupBalance: '',
  });
  const [merchantSearchTerm, setMerchantSearchTerm] = useState('');
  const [merchantSearchResults, setMerchantSearchResults] = useState([]);
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const [submittingTopup, setSubmittingTopup] = useState(false);

  // State for API data
  const [topupData, setTopupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  // State for filtering
  const [filters, setFilters] = useState({
    searchQuery: '',
    dateFrom: '',
    dateTo: '',
    keyword: '',
  });

  // Fetch Topup Balance data from API
  const fetchTopupCashback = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        page: params.page || 1,
        size: params.size || 20,
        type: 'N',
      };

      if (params.keyword) {
        requestParams.keyword = params.keyword;
      }

      if (params.dateFrom && params.dateTo) {
        requestParams.startDate = params.dateFrom;
        requestParams.endDate = params.dateTo;
      }

      const response = await merchantService.balanceHistory(requestParams);

      setTopupData(response.data?.data || []);
      setPagination({
        page: response.data?.page || 1,
        pageCount: response.data?.pageCount || 1,
        size: response.data?.size || 20,
        count: response.data?.count || 0,
        total: response.data?.total || 0,
      });
    } catch (err) {
      console.error('Failed to fetch Topup Balance data:', err);
      setError('Failed to load Topup Balance data. Please try again.');
      setTopupData([]);
      setPagination(prev => ({ ...prev, count: 0, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchFocus = useCallback(() => {
    setSearchFocused(true);
  }, []);

  const handleSearchChange = useCallback(e => {
    const value = e.target.value;
    setSearchTerm(value);
    setFilters(prev => ({ ...prev, searchQuery: value, keyword: value }));
  }, []);

  const handleSearchBlur = useCallback(() => {
    setSearchFocused(false);
  }, []);

  // Handle date range filtering
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: startDate,
      dateTo: endDate,
    }));
  }, []);

  // Apply filters
  const applyFilters = useCallback(async () => {
    const hasKeyword = filters.keyword;
    const hasDateRange = filters.dateFrom && filters.dateTo;

    const requestParams = {
      page: 1,
      size: pagination.size,
    };

    if (hasKeyword) {
      requestParams.keyword = filters.keyword;
    }

    if (hasDateRange) {
      requestParams.dateFrom = filters.dateFrom;
      requestParams.dateTo = filters.dateTo;
    }

    await fetchTopupCashback(requestParams);
  }, [
    filters.keyword,
    filters.dateFrom,
    filters.dateTo,
    fetchTopupCashback,
    pagination.size,
  ]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setFilters({
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
      keyword: '',
    });
    fetchTopupCashback({
      page: 1,
      size: pagination.size,
    });
  }, [fetchTopupCashback, pagination.size]);

  // Handle pagination
  const handlePageChange = useCallback(
    newPage => {
      const hasKeyword = filters.keyword;
      const hasDateRange = filters.dateFrom && filters.dateTo;

      const requestParams = {
        page: newPage,
        size: pagination.size,
      };

      if (hasKeyword) {
        requestParams.keyword = filters.keyword;
      }

      if (hasDateRange) {
        requestParams.dateFrom = filters.dateFrom;
        requestParams.dateTo = filters.dateTo;
      }

      fetchTopupCashback(requestParams);
    },
    [
      fetchTopupCashback,
      filters.keyword,
      filters.dateFrom,
      filters.dateTo,
      pagination.size,
    ]
  );

  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      fetchTopupCashback({ page: 1, size: 20 });
    }
  }, [fetchTopupCashback]);

  useEffect(() => {
    if (startDate && endDate) {
      handleDateRangeChange(startDate, endDate);
    }
  }, [startDate, endDate, handleDateRangeChange]);

  // Topup Balance modal handlers
  const handleOpenTopupModal = useCallback(() => {
    setShowTopupModal(true);
    setTopupFormData({ merchant: null, topupBalance: '' });
    setMerchantSearchTerm('');
    setMerchantSearchResults([]);
  }, []);

  const handleCloseTopupModal = useCallback(() => {
    setShowTopupModal(false);
    setTopupFormData({ merchant: null, topupBalance: '' });
    setMerchantSearchTerm('');
    setMerchantSearchResults([]);
  }, []);

  const handleMerchantSearch = useCallback(async query => {
    setMerchantSearchTerm(query);

    if (query.length < 2) {
      setMerchantSearchResults([]);
      setShowMerchantDropdown(false);
      return;
    }

    try {
      const response = await merchantService.searchMerchantsAdmin({
        keyword: query,
        page: 1,
        size: 20,
        order: '',
      });
      setMerchantSearchResults(response.data?.data || []);
      setShowMerchantDropdown(true);
    } catch (error) {
      console.error('Failed to search merchants:', error);
      setMerchantSearchResults([]);
      setShowMerchantDropdown(false);
    }
  }, []);

  const handleSelectMerchant = useCallback(merchant => {
    setTopupFormData(prev => ({ ...prev, merchant }));
    setMerchantSearchTerm(merchant.name);
    setShowMerchantDropdown(false);
  }, []);

  const handleTopupBalanceChange = useCallback(value => {
    setTopupFormData(prev => ({ ...prev, topupBalance: value }));
  }, []);

  const formatLocalDateTime = utcDateString => {
    if (!utcDateString) return 'N/A';
    try {
      const date = new Date(utcDateString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return utcDateString;
    }
  };

  const handleConfirmTopup = useCallback(
    e => {
      e.preventDefault();

      if (!topupFormData.merchant || !topupFormData.topupBalance) {
        return;
      }

      setShowConfirmModal(true);
    },
    [topupFormData]
  );

  const handleSubmitTopup = useCallback(async () => {
    setShowConfirmModal(false);
    setSubmittingTopup(true);

    try {
      await merchantService.topupBalance({
        merchantId: topupFormData.merchant.id,
        amount: parseFloat(topupFormData.topupBalance),
        currency: 'EUR',
        reference: 'Topup manual CS',
        confirm: true,
        preview: false,
      });

      console.log('Topup submitted successfully:', topupFormData);
      handleCloseTopupModal();
      fetchTopupCashback({ page: 1, size: pagination.size });
    } catch (err) {
      console.error('Failed to submit topup:', err);
      const errorMessage =
        err.message || 'Failed to submit topup. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmittingTopup(false);
    }
  }, [
    topupFormData,
    handleCloseTopupModal,
    fetchTopupCashback,
    pagination.size,
  ]);

  const handleCancelConfirm = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  const topupBalanceOptions = [
    { value: '50', label: '50 EUR' },
    { value: '100', label: '100 EUR' },
    { value: '200', label: '200 EUR' },
    { value: '500', label: '500 EUR' },
  ];

  return (
    <div>
      <PageHeader
        title='Topup Balance'
        subtitle='Manage merchant topup balance transactions'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Topup Balance' },
        ]}
        action={
          <button
            onClick={handleOpenTopupModal}
            className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            Topup Balance
          </button>
        }
      />

      {/* Search and Filter Section */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {/* Search Bar */}
          <div className='relative'>
            <label
              htmlFor='search'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search Merchant
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='search'
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder='Search by merchant name or keyword...'
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-md text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${searchFocused ? 'border-blue-300' : 'border-gray-300'}
                `}
              />
            </div>
          </div>

          {/* Date Range Filter */}
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onDateRangeChange={({ startDate: start, endDate: end }) => {
              handleDateRangeChange(start, end);
            }}
            placeholder='Select date range'
            label={{
              start: 'Date From',
              end: 'Date To',
            }}
          />

          {/* Filter Actions */}
          <div className='flex flex-col justify-end md:col-span-2 lg:col-span-1'>
            <div className='flex flex-col sm:flex-row gap-2'>
              <button
                onClick={clearFilters}
                className='flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500'
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                disabled={loading}
                className='flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? 'Applying...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
          <div className='flex items-center'>
            <svg
              className='w-5 h-5 text-red-400 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <p className='text-sm text-red-700'>{error}</p>
            <button
              onClick={() => setError(null)}
              className='ml-auto text-red-400 hover:text-red-600'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Topup Balance Table */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
          <div>
            <h3 className='text-lg font-medium text-gray-900 flex items-center gap-2'>
              Topup Balance List
              {loading && (
                <svg
                  className='animate-spin h-4 w-4 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h3>
            {pagination.total > 0 && (
              <p className='text-sm text-gray-500 mt-1'>
                Showing {(pagination.page - 1) * pagination.size + 1} to{' '}
                {Math.min(pagination.page * pagination.size, pagination.total)}{' '}
                of {pagination.total} transactions
              </p>
            )}
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Merchant Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Merchant ID
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Top-up Amount (EUR)
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Reference Note
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Admin Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Timestamp
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction ID
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {topupData.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan='7'
                    className='px-6 py-8 text-center text-gray-500'
                  >
                    <div className='flex flex-col items-center'>
                      <svg
                        className='w-12 h-12 text-gray-300 mb-3'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      <p className='text-lg font-medium'>
                        No topup balance data found
                      </p>
                      <p className='text-sm'>
                        topup balance transactions will appear here once
                        available.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                topupData.map((item, index) => (
                  <tr key={index}>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-gray-900'>
                        {item.companyName}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-mono'>
                      {item.companyId}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                      {item.balanceChange}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {item.reference}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {item.createdByName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {formatLocalDateTime(item.createdAt)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-mono'>
                      {item.id}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pageCount > 1 && (
          <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
            <div className='flex-1 flex justify-between sm:hidden'>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pageCount || loading}
                className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next
              </button>
            </div>
            <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
              <div>
                <p className='text-sm text-gray-700'>
                  Page <span className='font-medium'>{pagination.page}</span> of{' '}
                  <span className='font-medium'>{pagination.pageCount}</span>
                </p>
              </div>
              <div>
                <nav
                  className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                  aria-label='Pagination'
                >
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                    className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Previous</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from(
                    { length: Math.min(5, pagination.pageCount) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.pageCount <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pageCount - 2) {
                        pageNum = pagination.pageCount - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === pagination.page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={
                      pagination.page >= pagination.pageCount || loading
                    }
                    className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Next</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Topup Balance Modal */}
      {showTopupModal && (
        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
            {/* Background overlay */}
            <div
              className='fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75'
              onClick={handleCloseTopupModal}
            />

            {/* Modal panel */}
            <div className='inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-medium text-gray-900'>
                  Topup Balance
                </h3>
                <button
                  onClick={handleCloseTopupModal}
                  className='text-gray-400 hover:text-gray-600 focus:outline-none'
                >
                  <svg
                    className='w-5 h-5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleConfirmTopup}>
                {/* Merchant Autocomplete */}
                <div className='mb-4'>
                  <label
                    htmlFor='merchant'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Merchant
                  </label>
                  <div className='relative'>
                    <input
                      type='text'
                      id='merchant'
                      value={merchantSearchTerm}
                      onChange={e => handleMerchantSearch(e.target.value)}
                      onFocus={() =>
                        merchantSearchTerm.length >= 2 &&
                        setShowMerchantDropdown(true)
                      }
                      onBlur={() =>
                        setTimeout(() => setShowMerchantDropdown(false), 200)
                      }
                      placeholder='Search merchant by name...'
                      className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                    {showMerchantDropdown && (
                      <div className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto'>
                        {merchantSearchResults.length === 0 ? (
                          <div className='px-4 py-3 text-sm text-gray-500'>
                            No merchants found
                          </div>
                        ) : (
                          merchantSearchResults.map(merchant => (
                            <button
                              key={merchant.id}
                              type='button'
                              onClick={() => handleSelectMerchant(merchant)}
                              className='w-full px-4 py-2 text-left text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100'
                            >
                              <div className='font-medium text-gray-900'>
                                {merchant.name}
                              </div>
                              <div className='text-gray-500 text-xs'>
                                {merchant.address}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Topup Balance Input */}
                <div className='mb-6'>
                  <label
                    htmlFor='topupBalance'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Topup Balance
                  </label>
                  <input
                    type='text'
                    id='topupBalance'
                    value={topupFormData.topupBalance}
                    onChange={e => {
                      const value = e.target.value;
                      if (
                        value === '' ||
                        (/^\d*\.?\d*$/.test(value) && value.length <= 15)
                      ) {
                        handleTopupBalanceChange(value);
                      }
                    }}
                    maxLength={15}
                    placeholder='Enter amount (must be greater than 0)'
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                {/* Form Actions */}
                <div className='flex justify-end gap-3'>
                  <button
                    type='button'
                    onClick={handleCloseTopupModal}
                    className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={
                      !topupFormData.merchant ||
                      !topupFormData.topupBalance ||
                      submittingTopup
                    }
                    className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {submittingTopup ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
            {/* Background overlay */}
            <div
              className='fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75'
              onClick={handleCancelConfirm}
            />

            {/* Modal panel */}
            <div className='inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg'>
              <div className='flex items-center justify-center mb-4'>
                <div className='flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full'>
                  <svg
                    className='w-6 h-6 text-yellow-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                    />
                  </svg>
                </div>
              </div>

              <h3 className='text-lg font-medium text-gray-900 text-center mb-2'>
                Confirm Topup Balance
              </h3>

              <div className='mt-4 mb-6'>
                <p className='text-sm text-gray-500 text-center mb-4'>
                  Are you sure you want to topup the following amount?
                </p>
                <div className='bg-gray-50 rounded-lg p-4 space-y-2'>
                  <div className='flex justify-between'>
                    <span className='text-sm font-medium text-gray-700'>
                      Merchant:
                    </span>
                    <span className='text-sm text-gray-900'>
                      {topupFormData.merchant?.name}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm font-medium text-gray-700'>
                      Amount:
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {topupFormData.topupBalance} EUR
                    </span>
                  </div>
                </div>
              </div>

              <div className='flex justify-end gap-3'>
                <button
                  type='button'
                  onClick={handleCancelConfirm}
                  disabled={submittingTopup}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleSubmitTopup}
                  disabled={submittingTopup}
                  className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {submittingTopup ? 'Submitting...' : 'Confirm Topup'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopupCashbackPage;
